import { createContext, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { styled } from "@pigment-css/react";
import { Toast } from "./Toast";
import { ToastOverlay } from "./ToastOverlay";

/** Delay before a confirmation pill appears, so it slides in a beat after a screen transition. */
export const TOAST_TRANSITION_DELAY = 300;

export interface ToastContent {
  message: string;
  /** Icon tint. "destructive" (red) for delete confirmations; defaults to success green. */
  tone?: "success" | "destructive";
  /** Leading icon. Defaults per tone: a checkmark, or a cross for "destructive". */
  icon?: ReactNode;
  tintMessage?: boolean;
  /** How long the toast stays visible, in ms. */
  durationMs?: number;
  /** Delay before the toast appears, e.g. TOAST_TRANSITION_DELAY after a navigation. */
  initialDelay?: number;
}

export interface ToastContextValue {
  /** Show a transient toast. Fire-and-forget; it auto-dismisses. */
  showToast: (content: ToastContent) => void;
  /** Portal target for banners a caller renders itself. Null until the overlay has mounted. */
  bannerHost: HTMLElement | null;
}

export const ToastContext = createContext<ToastContextValue | null>(null);

// Layout-invisible, so banners portalled in here are laid out by the overlay itself and pick up its
// column and gap rather than a nested box's.
const BannerHost = styled("div")({ display: "contents" });

/**
 * Owns where toasts appear and how they stack, for both of the ways one gets on screen. A transient
 * confirmation is fired as data through `showToast`, because callers fire it as they unmount (a
 * detail view closing on delete) and it has to outlive them. A banner carrying an action is rendered
 * by its owner through `UndoToastSlot` instead, because its handler closes over that owner's state
 * and must die with it.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [content, setContent] = useState<ToastContent | null>(null);
  const [open, setOpen] = useState(false);
  // Bumped per show so a toast fired while another is visible remounts with a fresh timer + entrance.
  const [nonce, setNonce] = useState(0);
  const delayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [bannerHost, setBannerHost] = useState<HTMLElement | null>(null);

  const showToast = useCallback((next: ToastContent) => {
    if (delayRef.current) clearTimeout(delayRef.current);
    const run = () => {
      setContent(next);
      setOpen(true);
      setNonce((n) => n + 1);
    };
    if (next.initialDelay && next.initialDelay > 0) delayRef.current = setTimeout(run, next.initialDelay);
    else run();
  }, []);

  useEffect(
    () => () => {
      if (delayRef.current) clearTimeout(delayRef.current);
    },
    [],
  );

  const value = useMemo<ToastContextValue>(() => ({ showToast, bannerHost }), [showToast, bannerHost]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastOverlay>
        <Toast
          key={nonce}
          open={open}
          message={content?.message ?? ""}
          tone={content?.tone}
          icon={content?.icon}
          tintMessage={content?.tintMessage}
          durationMs={content?.durationMs}
          onClose={() => setOpen(false)}
        />
        <BannerHost ref={setBannerHost} />
      </ToastOverlay>
    </ToastContext.Provider>
  );
}
